import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  useToast,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { IoMdLock } from "react-icons/io";
import { BsUnlockFill } from "react-icons/bs";
import axios from 'axios';
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Wallets from "./pages/Wallets";
import Friends from "./pages/Friends";
import Profile from "./pages/Profile";
import CoinEarnings from "./pages/CoinEarnings";
import VoiceApproval from "./pages/VoiceApproval";
import Withdrawals from "./pages/Withdrawals";
import CoinPackages from "./pages/CoinPackages";
import Gifts from "./pages/Gifts";
import CallConfig from "./pages/CallConfig";
import CallTransactions from "./pages/CallTransactions";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import ConversionSettings from "./pages/ConversionSettings";
import Analytics from "./pages/Analytics";
import AdvancedReports from "./pages/AdvancedReports";
import AdminDashboard from './pages/AdminDashboard';
import PurchaseHistory from './pages/PurchaseHistory';
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import UserCoinDetails from './pages/UserCoinDetails';
import UserCallHistory from './pages/UserCallHistory';
import PurchaseHistoryDetails from './pages/PurchaseHistoryDetails';
import PushNotifications from './pages/PushNotifications';
import UserActivity from './pages/UserActivity';
import Leaderboard from './pages/Leaderboard';
import SuperAdminProtected from './components/utilities/SuperAdminProtected';
import Unauthorized from './pages/Unauthorized';
import AdminBankVerification from './pages/AdminBankVerification';
import LanguageManagement from "./pages/LanguageManagement";

// API configuration

//depricated prod url

//const API_URL = 'http://localhost:5050'; // For local development only



 const API_URL = 'https://prod-api.besty.arrowcoders.in'; // For production development only


// Set up axios defaults
axios.defaults.baseURL = API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['x-api-key'] = 'besty_super_secret_key_2026';

// Axios interceptor to add auth token to requests
axios.interceptors.request.use(
  config => {
     config.headers["x-api-key"] = 'besty_super_secret_key_2026';
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = React.createContext();
export { AuthContext };

const useAuth = () => {
  return React.useContext(AuthContext);
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const storedUser = localStorage.getItem('adminUser');

    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);


      const response = await axios.post('/api/v1/admin/auth/login', { email, password });

      if (response.data.success) {
        const { token, user } = response.data;
        localStorage.setItem('adminToken', token);
        localStorage.setItem('adminUser', JSON.stringify(user));
        setUser(user);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="pink.400" />
      </Center>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading, error: authError } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (authError) setError(authError);
  }, [authError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await login(email, password);
    if (success) {
      toast({
        title: "Login successful",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/");
    } else {
      toast({
        title: "Login failed",
        description: error || "Invalid email or password",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="var(--black)"
      color="var(--yellow)"
      p={4}
    >
      <Box
        p={8}
        maxWidth="500px"
        borderWidth={1}
        borderRadius={12}
        boxShadow="lg"
        bg="var(--black)"
        borderColor="var(--yellow)"
      >
        <Box textAlign="center" mb={6}>
          <Heading
            fontSize="4xl"
            mb={2}
            color="var(--yellow)"
            // subtle text-shadow for a nicer look on dark bg
            style={{ textShadow: "0 1px 0 rgba(0,0,0,0.4)" }}
          >
            Besty Admin
          </Heading>
          <Text fontSize="sm" color="var(--yellow)" opacity={0.8}>
            Admin Panel
          </Text>
        </Box>

        <form onSubmit={handleSubmit}>
          {error && (
            <Text color="red.400" mb={4}>
              {error}
            </Text>
          )}
          

          <FormControl id="email" mb={4}>
            <FormLabel color="var(--yellow)" fontWeight="medium">
              Email
            </FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              required
              bg="transparent"
              border="1px solid"
              borderColor="var(--yellow)"
              color="var(--yellow)"
              _placeholder={{ color: "var(--yellow)", opacity: 0.6 }}
              _focus={{
                boxShadow: "0 0 0 3px rgba(250,204,21,0.12)",
                borderColor: "var(--yellow)",
              }}
            />
          </FormControl>



          <FormControl id="password" mb={6}>
            <FormLabel color="var(--yellow)" fontWeight="medium">
              Password
            </FormLabel>

            <Box position="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                bg="transparent"
                border="1px solid"
                borderColor="var(--yellow)"
                color="var(--yellow)"
                pr="45px"
                _placeholder={{ color: "var(--yellow)", opacity: 0.6 }}
                _focus={{
                  boxShadow: "0 0 0 3px rgba(250,204,21,0.12)",
                  borderColor: "var(--yellow)",
                }}
              />

              {/* Eye Toggle */}
              <Box
                position="absolute"
                top="50%"
                right="12px"
                transform="translateY(-50%)"
                cursor="pointer"
                color="var(--yellow)"
                fontSize="20px"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <BsUnlockFill className="text-xl" /> : <IoMdLock className="text-xl" />}
              </Box>
            </Box>
          </FormControl>


          <Button
            type="submit"
            width="full"
            mb={4}
            bg="var(--yellow)"
            color="var(--black)"
            _hover={{ opacity: 0.95 }}
            isLoading={loading}
            loadingText="Signing in"
          >
            Sign In
          </Button>
        </form>
      </Box>
    </Flex>
  );
};


// Main App Component
function App() {
  return (

    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50">
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/users" element={<Users />} />
                      <Route path="/coin-earnings" element={<CoinEarnings />} />
                      <Route path="/voice-approval" element={<VoiceApproval />} />
                      <Route path="/withdrawals" element={<Withdrawals />} />
                      <Route path="/coin-packages" element={<CoinPackages />} />
                      <Route path="/conversion-settings" element={<SuperAdminProtected><ConversionSettings /></SuperAdminProtected>
                      }
                      />
                      <Route path="/gifts" element={<Gifts />} />
                      <Route path="/call-config" element={<SuperAdminProtected><CallConfig /></SuperAdminProtected>} />
                      <Route path="/call-transactions" element={<CallTransactions />} />
                      <Route path="/analytics" element={<Analytics />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="/advanced-reports" element={<AdvancedReports />} />
                      <Route path="/settings" element={<SuperAdminProtected><Settings /></SuperAdminProtected>} />
                      <Route path="/wallets" element={<Wallets />} />
                      <Route path="/friends" element={<Friends />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/purchase-history" element={<PurchaseHistory />} />
                      <Route path="/admin-managment" element={<SuperAdminProtected><AdminDashboard /></SuperAdminProtected>} />
                      <Route path="/call-transactions/:userId" element={<UserCallHistory />} />
                      <Route path="/purchase-history/:userId" element={<PurchaseHistoryDetails />} />
                      <Route path="/unauthorized" element={<Unauthorized />} />
                      <Route path="/coin-earnings/user/:id" element={<UserCoinDetails />} />
                      <Route path="/push-notifications" element={<PushNotifications />} />
                      <Route path="/user-activity" element={<UserActivity />} />
                      <Route path="/leaderboard" element={<Leaderboard />} />
                       <Route path="/bankVerifcation" element={<AdminBankVerification />} />
                       <Route path="/languages" element={<LanguageManagement />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </AuthProvider>
    </div>
  );
}
export default App;
